import { Link } from 'react-router-dom';
import { useAuthStore, can } from '../auth/store';
import { useExpiringItems } from '../api/queries/expiring-items';
import { useKpiDashboard } from '../api/queries/kpi';
import { fmtDate } from '../utils/date';
import './DashboardPage.css';

function daysUntil(epochMs: number): number {
  const ms = epochMs - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

import { formatMoney } from '../utils/money';

function fmtHours(hours: string | undefined): string {
  if (hours === undefined) return '—';
  const n = Number(hours);
  return Number.isFinite(n) ? n.toFixed(2) : hours;
}

interface KpiCardProps {
  label: string;
  value: string;
  meta?: string;
  tone?: 'default' | 'warn' | 'success' | 'danger';
  to?: string;
}

function KpiCard({ label, value, meta, tone = 'default', to }: KpiCardProps): JSX.Element {
  const inner = (
    <>
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value kpi-${tone}`}>{value}</div>
      {meta && <div className="kpi-meta">{meta}</div>}
    </>
  );
  if (to) {
    return (
      <Link to={to} className="kpi-card kpi-card-link">
        {inner}
      </Link>
    );
  }
  return <div className="kpi-card">{inner}</div>;
}

export function DashboardPage(): JSX.Element {
  const me = useAuthStore((s) => s.me)!;
  const showExpiring = can('expiring_item.read');
  const expiringQuery = useExpiringItems(
    showExpiring ? { within_days: 60, include_expired: true } : {},
  );
  const kpiQuery = useKpiDashboard();
  const kpi = kpiQuery.data;

  const expiringRaw = expiringQuery.data?.expiring_items ?? [];
  const expiringSoon = showExpiring
    ? expiringRaw
        .filter((i) => {
          const d = daysUntil(i.expires_at);
          return d <= i.reminder_days_before;
        })
        .slice(0, 6)
    : [];

  return (
    <div className="dashboard-page">
      <section className="dashboard-welcome">
        <h2>
          Welcome back, <span className="dashboard-name">{me.user.display_name}</span>
        </h2>
      </section>

      {kpi && (
        <section className="kpi-grid">
          {/* Row 1 — activity metrics (clients / projects / hours) */}
          {kpi.clients && (
            <KpiCard
              label="Active clients"
              value={String(kpi.clients.active)}
              to="/clients"
            />
          )}
          {kpi.projects && (
            <KpiCard
              label="Active projects"
              value={String(kpi.projects.active)}
              meta={`${kpi.projects.total} total`}
              to="/projects"
            />
          )}
          {kpi.time && (
            <KpiCard
              label="Hours this week"
              value={fmtHours(kpi.time.this_week_hours)}
              meta={`${fmtHours(kpi.time.unbilled_hours)} hr · ${formatMoney(kpi.time.unbilled_value)} unbilled`}
              to="/time-entries"
            />
          )}

          {/* Money band — wraps onto the next row(s) */}
          {kpi.invoices && (
            <KpiCard
              label="Outstanding"
              value={formatMoney(kpi.invoices.outstanding_total)}
              meta={`${kpi.invoices.outstanding_count} invoice${kpi.invoices.outstanding_count === 1 ? '' : 's'}`}
              tone={kpi.invoices.overdue_count > 0 ? 'warn' : 'default'}
              to="/invoices"
            />
          )}
          {kpi.invoices && kpi.invoices.overdue_count > 0 && (
            <KpiCard
              label="Overdue"
              value={String(kpi.invoices.overdue_count)}
              meta="invoices past due"
              tone="danger"
              to="/invoices"
            />
          )}
          {kpi.invoices && (
            <KpiCard
              label="Paid (30 days)"
              value={formatMoney(kpi.invoices.paid_30d_total)}
              tone="success"
              to="/invoices"
            />
          )}
          {kpi.expenses && (
            <KpiCard
              label="Expenses this week"
              value={formatMoney(kpi.expenses.this_week_total)}
              meta={
                Number(kpi.expenses.reimbursable_total) > 0
                  ? `${formatMoney(kpi.expenses.reimbursable_total)} reimbursable owed`
                  : undefined
              }
              tone={Number(kpi.expenses.reimbursable_total) > 0 ? 'warn' : 'default'}
              to="/expenses"
            />
          )}
          {kpi.retainers && kpi.retainers.active_count > 0 && (
            <KpiCard
              label="Retainer balances"
              value={formatMoney(kpi.retainers.total_balance)}
              meta={`${kpi.retainers.active_count} active`}
              tone="success"
              to="/clients"
            />
          )}

          {/* Renewal alert — trails the money band so it never splits the rows above */}
          {kpi.expiring && (kpi.expiring.within_30d > 0 || kpi.expiring.expired > 0) && (
            <KpiCard
              label="Expiring soon"
              value={String(kpi.expiring.within_30d)}
              meta={
                kpi.expiring.expired > 0
                  ? `${kpi.expiring.expired} already expired`
                  : 'within reminder window'
              }
              tone={kpi.expiring.expired > 0 ? 'danger' : 'warn'}
              to="/expiring"
            />
          )}
        </section>
      )}

      {showExpiring && expiringSoon.length > 0 && (
        <section className="dashboard-card dashboard-expiring">
          <header className="dashboard-expiring-header">
            <h2>Expiring Soon</h2>
            <Link to="/expiring" className="dashboard-link">View all →</Link>
          </header>
          <ul className="dashboard-expiring-list">
            {expiringSoon.map((i) => {
              const d = daysUntil(i.expires_at);
              const expired = d < 0;
              return (
                <li
                  key={i.id}
                  className={`dashboard-expiring-row ${expired ? 'dashboard-expiring-expired' : ''}`}
                >
                  <span className="dashboard-expiring-name">{i.name}</span>
                  <span className="dashboard-expiring-meta">
                    {fmtDate(i.expires_at)}
                    <span className={`dashboard-expiring-pill ${expired ? 'dashboard-pill-danger' : 'dashboard-pill-warn'}`}>
                      {expired ? `Expired ${Math.abs(d)}d ago` : d === 0 ? 'Today' : `${d}d`}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
